// Typed wrapper for the /v1/stats admin dashboard endpoint (Phase 06.1).
//
// BFF rule (CLAUDE.md rule 1): goes through apiClient — the single axios
// instance that injects Authorization: Bearer <token> + X-Merchant-ID.
//
// Money handling (CLAUDE.md rule 6): settled_minor_by_currency values come
// over the wire as JSON integers; apiClient's json-bigint parser upgrades any
// value past Number.MAX_SAFE_INTEGER to bigint. Lifetime aggregates are the
// most likely place to ever exceed that ceiling — exactly why this endpoint
// must round-trip through bigint (CLAUDE.md rule 6).
import { apiClient } from "./client";
import type { DateRangeBounds } from "@/utils/dateRange";

interface RawStalePending {
  count: number;
  /** JSON integer (INR paise); bigint past 2^53. */
  total_minor: number | bigint;
  oldest_age_hours: number;
}

interface RawStats {
  /** Merchant the payout figures are scoped to; null = platform-wide. */
  merchant_id?: number | null;
  /** Echoed [from, to) created_at window, RFC 3339 UTC; null = unbounded. */
  from?: string | null;
  to?: string | null;
  merchant_count: number;
  payouts: PayoutCounts;
  stale_pending: RawStalePending;
  /** JSON integers (INR paise); bigint past 2^53. */
  settled_minor_by_currency: Record<string, number | bigint>;
  /** status → currency → paise over the window. */
  amount_minor_by_status?: Record<string, Record<string, number | bigint>>;
}

/**
 * StalePending — payouts stuck in 'pending' for >24h.
 *
 * The reconciler keeps polling pending rows indefinitely; this block
 * surfaces the ones upstream has never resolved so an operator can
 * see them at a glance and escalate to IDFC support with the
 * provider_txn_id. count==0 means the queue is healthy.
 *
 * total_minor is bigint paise (CLAUDE.md rule 6).
 */
export interface StalePending {
  count: number;
  total_minor: bigint;
  oldest_age_hours: number;
}

/**
 * Stats — aggregates for the admin Dashboard. Platform-wide when no
 * merchant is selected; scoped to the current merchant otherwise (the
 * backend reads X-Merchant-ID). merchant_count is platform-wide either way.
 *
 * settled_minor_by_currency values are bigint paise (CLAUDE.md rule 6 —
 * money is bigint end-to-end in the SPA). Display via fmtCcyMinor.
 */
/**
 * PayoutCounts — transaction counts by status, platform-wide.
 *
 * Every status in the backend enum has a field, and total is the true row
 * count. They add up: needs_approval + pending + success + failure === total.
 * If they ever do not, the backend has a status this type has not been taught
 * — which is worth noticing rather than papering over, so the dashboard shows
 * the shortfall rather than silently dropping it.
 *
 * needs_approval is optional on the wire so a panel deployed ahead of the
 * backend does not break; treat undefined as 0.
 */
export interface PayoutCounts {
  /** Submitted, waiting on an admin. No money has moved. */
  needs_approval?: number;
  /** Dispatched and unresolved. On a file rail this is waiting on an
   *  operator to send the file, not on a bank. */
  pending: number;
  success: number;
  failure: number;
  total: number;
}

export interface Stats {
  /** Merchant the payout figures are scoped to; null = platform-wide. */
  merchant_id: number | null;
  /** Echoed [from, to) created_at window; null = unbounded on that side. */
  from: string | null;
  to: string | null;
  merchant_count: number;
  payouts: PayoutCounts;
  stale_pending: StalePending;
  settled_minor_by_currency: Record<string, bigint>;
  /**
   * status → currency → paise over the window: the money behind each count
   * in `payouts`. Computed by the same backend function as the payouts
   * list's status_amounts_minor, so the two cannot disagree.
   */
  amount_minor_by_status: Record<string, Record<string, bigint>>;
}

/**
 * getStats — GET /v1/stats?from=&to=
 * Admin-only. Scoped by X-Merchant-ID when one is set, and to the
 * created_at window when one is given (counts + settled sums only; the
 * stale-pending sweep and merchant_count ignore it). Converts settled
 * amounts + stale_pending total from JSON number to bigint at the
 * deserialization boundary.
 */
export async function getStats(range: DateRangeBounds = {}): Promise<Stats> {
  const resp = await apiClient.get<RawStats>("/v1/stats", {
    params: { from: range.from, to: range.to },
  });
  const settled: Record<string, bigint> = {};
  for (const [ccy, v] of Object.entries(resp.data.settled_minor_by_currency)) {
    settled[ccy] = BigInt(v);
  }
  // Defensive default for old backends that don't yet ship stale_pending.
  // The query polls every 30s, so during a rolling deploy the panel
  // would briefly receive responses without the field — coerce to a
  // safe zero-count instead of letting `undefined.count` blow up the
  // Dashboard render.
  const sp = resp.data.stale_pending ?? {
    count: 0,
    total_minor: 0,
    oldest_age_hours: 0,
  };
  const byStatus: Record<string, Record<string, bigint>> = {};
  for (const [st, m] of Object.entries(resp.data.amount_minor_by_status ?? {})) {
    byStatus[st] = {};
    for (const [ccy, v] of Object.entries(m)) byStatus[st][ccy] = BigInt(v);
  }
  return {
    merchant_id: resp.data.merchant_id ?? null,
    from: resp.data.from ?? null,
    to: resp.data.to ?? null,
    merchant_count: resp.data.merchant_count,
    payouts: resp.data.payouts,
    stale_pending: {
      count: sp.count,
      total_minor: BigInt(sp.total_minor),
      oldest_age_hours: sp.oldest_age_hours,
    },
    settled_minor_by_currency: settled,
    amount_minor_by_status: byStatus,
  };
}
